import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Timer, Thermometer } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Air Drying the Copper',
    content: 'Air drying allows the volatile acetone to evaporate at room temperature. Acetone has a boiling point of only 56C and high vapor pressure, so it evaporates quickly even without heating.',
    whatHappens: 'The acetone evaporates from the copper surface. You may notice the characteristic sweet smell of acetone vapor (work in fume hood or well-ventilated area).',
    observe: [
      'Acetone evaporates rapidly (1-2 minutes)',
      'Copper surface becomes visibly drier',
      'Sweet odor of evaporating acetone',
      'No visible liquid should remain after 5 minutes'
    ]
  },
  2: {
    title: 'Oven Drying at 80C',
    content: 'Gentle oven drying at 80C removes any remaining traces of moisture without oxidizing the copper. Higher temperatures (>100C) could oxidize Cu to CuO, adding mass and affecting yield calculations.',
    whatHappens: 'The copper is heated in a drying oven at 80C for about 15 minutes. This ensures complete removal of any adsorbed moisture.',
    observe: [
      'Temperature maintained at 80C (not higher)',
      'Copper may appear slightly shinier after drying',
      'No visible changes during drying',
      'Copper should be completely dry after 15 minutes'
    ]
  }
};

export default function Part8Drying() {
  const [airDrying, setAirDrying] = useState(false);
  const [airDried, setAirDried] = useState(false);
  const [ovenDrying, setOvenDrying] = useState(false);
  const [ovenDried, setOvenDried] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [airDryProgress, setAirDryProgress] = useState(0);
  const [ovenDryProgress, setOvenDryProgress] = useState(0);
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const id = useId();

  const completedSteps = [airDried, ovenDried].filter(Boolean).length;

  const handleStartAirDry = useCallback(() => {
    if (!airDrying && !airDried) {
      setAirDrying(true);

      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.02;
        setAirDryProgress(progress);
        if (progress >= 1) {
          clearInterval(interval);
          setAirDrying(false);
          setAirDried(true);
          setCurrentStep(2);
        }
      }, 60); // 5 minutes = 300 ticks
    }
  }, [airDrying, airDried]);

  const handleStartOvenDry = useCallback(() => {
    if (airDried && !ovenDrying && !ovenDried) {
      setOvenDrying(true);

      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.015;
        setOvenDryProgress(progress);
        if (progress >= 1) {
          clearInterval(interval);
          setOvenDrying(false);
          setOvenDried(true);
        }
      }, 50); // 15 minutes simulated
    }
  }, [airDried, ovenDrying, ovenDried]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part IX: Drying</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Air Dry (5 min)', hint: 'Allow acetone to evaporate', done: airDried },
            { id: 2, title: 'Oven Dry (15 min)', hint: 'Heat at 80C to complete drying', done: ovenDried }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Timer display */}
        {(airDrying || ovenDrying) && (
          <div className="timer-display">
            <Timer size={16} />
            <span>{airDrying ? 'Air Drying' : 'Oven Drying'}...</span>
            <div className="timer-bar">
              <div
                className="timer-fill"
                style={{
                  width: `${(airDrying ? airDryProgress : ovenDryProgress) * 100}%`
                }}
              />
            </div>
            <span>{Math.round((airDrying ? airDryProgress : ovenDryProgress) * 100)}%</span>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Copper Drying"
        completedCount={completedSteps}
        totalSteps={2}
        equipment={['Evaporating dish with copper', 'Oven']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Evaporating dish */}
            <div
              className={`equipment-station ${currentStep === 1 && !airDried ? 'clickable' : ''}`}
              onClick={handleStartAirDry}
              style={{ cursor: currentStep === 1 && !airDried && !airDrying ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 160 120" width={180} height={140}>
                <defs>
                  <linearGradient id={`dish-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#E8E0D5" />
                    <stop offset="50%" stopColor="#F5F0E8" />
                    <stop offset="100%" stopColor="#E8E0D5" />
                  </linearGradient>
                </defs>

                {/* Evaporation lines when air drying */}
                {airDrying && (
                  <g>
                    {[...Array(5)].map((_, i) => (
                      <line
                        key={i}
                        x1={50 + i * 15}
                        y1={60}
                        x2={50 + i * 15}
                        y2={30}
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth="2"
                        strokeDasharray="4,4"
                      >
                        <animate
                          attributeName="y2"
                          values="60;20;60"
                          dur={`${1.5 + i * 0.2}s`}
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="0.3;0.1;0"
                          dur={`${1.5 + i * 0.2}s`}
                          repeatCount="indefinite"
                        />
                      </line>
                    ))}
                  </g>
                )}

                {/* Porcelain dish */}
                <ellipse cx="80" cy="80" rx="65" ry="20" fill={`url(#dish-${id})`} stroke="#C8C0B5" strokeWidth="2" />
                <path d="M15 80 Q15 50 80 50 Q145 50 145 80" fill="none" stroke="#C8C0B5" strokeWidth="2" />

                {/* Copper */}
                <ellipse
                  cx="80"
                  cy="78"
                  rx="50"
                  ry="12"
                  fill={copperCycleColors.copperPrecipitate}
                  opacity={airDried ? 1 : 0.85}
                />

                {/* Wet sheen before drying */}
                {!airDried && (
                  <ellipse cx="80" cy="75" rx="40" ry="8" fill="rgba(255,255,255,0.2)" />
                )}

                {/* Shiny copper after drying */}
                {airDried && (
                  <g>
                    <ellipse cx="70" cy="75" rx="15" ry="5" fill="#D4A373" opacity="0.5" />
                  </g>
                )}

                {/* Boiling chips */}
                <circle cx="60" cy="76" r="2" fill={copperCycleColors.boilingChips} />
                <circle cx="100" cy="77" r="2" fill={copperCycleColors.boilingChips} />

                <text x="80" y="110" fontSize="10" fill="#E0E0E0" textAnchor="middle">
                  {airDrying ? 'Air Drying...' : airDried ? 'Air Dried' : 'Click to Air Dry'}
                </text>
              </svg>
            </div>

            {/* Oven */}
            <div
              className={`equipment-station ${currentStep === 2 && !ovenDried ? 'clickable' : ''}`}
              onClick={handleStartOvenDry}
              style={{ cursor: currentStep === 2 && !ovenDried && !ovenDrying ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 180 160" width={200} height={180}>
                <defs>
                  <linearGradient id={`oven-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#4A4A4A" />
                    <stop offset="100%" stopColor="#2A2A2A" />
                  </linearGradient>
                </defs>

                {/* Oven body */}
                <rect x="20" y="20" width="140" height="110" fill={`url(#oven-${id})`} rx="5" />

                {/* Door */}
                <rect x="30" y="30" width="120" height="70" fill="#333" rx="3" />

                {/* Window */}
                <rect
                  x="40"
                  y="40"
                  width="100"
                  height="50"
                  fill={ovenDrying ? 'rgba(255, 100, 50, 0.3)' : '#222'}
                  rx="2"
                />

                {/* Heat glow inside */}
                {ovenDrying && (
                  <g>
                    <ellipse cx="90" cy="65" rx="40" ry="20" fill="rgba(255, 100, 50, 0.2)">
                      <animate attributeName="opacity" values="0.2;0.4;0.2" dur="1s" repeatCount="indefinite" />
                    </ellipse>
                    {/* Sample inside */}
                    <ellipse cx="90" cy="70" rx="25" ry="8" fill={copperCycleColors.copperPrecipitate} />
                  </g>
                )}

                {/* Control panel */}
                <rect x="30" y="105" width="120" height="20" fill="#222" rx="2" />

                {/* Temperature display */}
                <rect x="40" y="108" width="40" height="14" fill="#001100" rx="1" />
                <text x="60" y="118" fontSize="10" fill="#00FF00" textAnchor="middle" fontFamily="monospace">
                  {ovenDrying ? '80' : airDried ? '25' : '--'}C
                </text>

                {/* Power indicator */}
                <circle cx="130" cy="115" r="4" fill={ovenDrying ? '#00FF00' : '#333'}>
                  {ovenDrying && (
                    <animate attributeName="opacity" values="1;0.6;1" dur="1.5s" repeatCount="indefinite" />
                  )}
                </circle>

                {/* Handle */}
                <rect x="85" y="98" width="10" height="6" fill="#555" rx="2" />

                <text x="90" y="150" fontSize="10" fill="#E0E0E0" textAnchor="middle">
                  Drying Oven (80C)
                </text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !airDrying && !airDried && (
              <div className="status-message info">
                <Timer size={20} />
                Click the dish to start air drying (5 min)
              </div>
            )}
            {airDrying && (
              <div className="status-message drying">
                <Timer size={20} />
                Air drying - acetone evaporating...
              </div>
            )}
            {currentStep === 2 && !ovenDrying && !ovenDried && (
              <div className="status-message info">
                <Thermometer size={20} />
                Click oven to dry at 80C (15 min)
              </div>
            )}
            {ovenDrying && (
              <div className="status-message drying">
                <Thermometer size={20} />
                Oven drying at 80C...
              </div>
            )}
            {ovenDried && (
              <div className="status-message success">
                <Check size={20} />
                Copper completely dried and ready for weighing!
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Proper Drying</h4>
            <p>
              Complete drying is essential for accurate mass measurement.
              Residual moisture would add to the apparent mass and give a falsely high yield.
            </p>
            <p>
              <strong>Air drying:</strong> Removes most of the volatile acetone.
            </p>
            <p>
              <strong>Oven drying (80C):</strong> Removes any remaining moisture without oxidizing the copper.
              Higher temperatures could oxidize Cu to CuO!
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 50px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .timer-display {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 16px;
            padding: 12px;
            background: var(--bg-elevated);
            border-radius: 8px;
            color: var(--text-primary);
          }

          .timer-bar {
            flex: 1;
            height: 8px;
            background: #333;
            border-radius: 4px;
            overflow: hidden;
          }

          .timer-fill {
            height: 100%;
            background: linear-gradient(90deg, var(--secondary), #4ADE80);
            transition: width 0.1s;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.drying {
            background: rgba(255, 159, 64, 0.2);
            color: #FF9F40;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .theory-box p strong {
            color: var(--text-primary);
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
